import { useCallback, useEffect, useState } from "react";
import {
    Button,
    Collapse,
    Input,
    IconButton,
    Typography,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import EditThemeDialog from "./dialog/edit-theme";
import { createSurveyCacheInvalidator } from "utils/invalidate-survey-caches";
import { deleteTheme } from "./utils/delete-theme";
import PopoverCheckboxes from "./filters/popover-checkboxes";
import FilterChips from "./filters/filter-chips";

export default function ThemingTabFilters({
    filtersOpen,
    setFiltersOpen,
    textSearch,
    setTextSearch,
    filters,
    addFilter,
    emptyFilter,
    setFilters,
    removeFilter,
    surveyQuestions,
    themesData,
    customResponsesFilterPager,
    handleEditThemeName,
    themesDataMutate,
    customResponsesFilterMutate,
    surveyId,
    sortedResponses,
    dropdownThemeCounts,
    dropdownQuestionCounts,
    counter,
}) {
    const [responseNoDisplay, setResponseNoDisplay] = useState(0);
    const [displayedQuestions, setDisplayedQuestions] = useState([]);
    const [selectedTheme, setSelectedTheme] = useState(null);

    const [showThemeOptionsDialog, setShowThemeOptionsDialog] = useState(false);
    const [showEditThemeDialog, setShowEditThemeDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [themeToDelete, setThemeToDelete] = useState(null);

    // Local invalidator matches the pattern in manage-themes-dialog.jsx so
    // the centralized deleteTheme helper can revalidate cross-cutting
    // caches (reports, sentiment) without requiring the parent to thread
    // an invalidateSurveyCaches prop through ThemingTabFilters.
    const invalidateSurveyCaches = createSurveyCacheInvalidator(surveyId);

    useEffect(() => {
        // Compute the displayed-response total from counter data when filters
        // are active; counter is the authoritative source of theme/question
        // counts and avoids re-counting on every render.
        if (counter && (
            filters?.questions?.length > 0 ||
            filters?.themes?.length > 0 ||
            filters?.texts?.length > 0 ||
            filters?.keywords?.length > 0 ||
            filters?.unthemed
        )) {
            let totalCount = 0;
            if (filters?.unthemed) {
                totalCount = counter.unthemed_count || 0;
                if (filters?.themes?.length > 0) {
                    const themeIds = filters.themes.map(t => String(t.id));
                    const themeCountSum = themeIds.reduce((sum, id) => {
                        return sum + (counter.theme_counts?.[id] || 0);
                    }, 0);
                    totalCount += themeCountSum;
                }
            } else if (filters?.themes?.length > 0) {
                const themeIds = filters.themes.map(t => String(t.id));
                totalCount = themeIds.reduce((sum, id) => {
                    return sum + (counter.theme_counts?.[id] || 0);
                }, 0);
            } else if (filters?.questions?.length > 0) {
                const questionNanoids = filters.questions.map(q => q.nanoid);
                totalCount = questionNanoids.reduce((sum, nanoid) => {
                    return sum + (counter.question_counts?.[nanoid] || 0);
                }, 0);
            } else {
                totalCount = customResponsesFilterPager?.total || sortedResponses.length;
            }
            setResponseNoDisplay(totalCount);
        } else if (
            sortedResponses?.length > 0 &&
            !(
                filters?.questions?.length < 1 &&
                filters?.themes?.length < 1 &&
                filters?.texts?.length < 1 &&
                filters?.keywords?.length < 1 &&
                !filters?.unthemed
            )
        ) {
            setResponseNoDisplay(sortedResponses.length);
        } else {
            setResponseNoDisplay(0);
        }
    }, [sortedResponses, filters, counter, customResponsesFilterPager]);

    useEffect(() => {
        if (surveyQuestions?.length > 0) {
            const noPresetOrPiiQuestions = surveyQuestions?.filter(
                (q) => q.question_type === "freeform" && !q.contains_pii,
            );
            setDisplayedQuestions(noPresetOrPiiQuestions);
        }
    }, [surveyQuestions]);

    const handleOpenOptions = (theme) => {
        setSelectedTheme(theme);
        setShowThemeOptionsDialog(true);
    };

    const handleOpenEdit = () => {
        setShowThemeOptionsDialog(false);
        setShowEditThemeDialog(true);
    };

    const handleOpenDelete = () => {
        setThemeToDelete(selectedTheme);
        setShowDeleteDialog(true);
        setShowThemeOptionsDialog(false);
    };

    const handleDeleteTheme = (id) =>
        deleteTheme({
            surveyId,
            themeId: id,
            themesDataMutate,
            customResponsesFilterMutate,
            invalidateSurveyCaches,
        });

    const questionsIsSelected = useCallback(
        (item) => filters?.questions?.includes(item),
        [filters?.questions],
    );
    const themesIsSelected = useCallback(
        (item) => filters?.themes?.includes(item),
        [filters?.themes],
    );

    const handleAddSearch = (e, values) => {
        const newId = filters?.texts?.length + 1;
        if (values?.search) {
            addFilter("texts", {
                id: newId,
                text: values.search,
            });
        }
    };

    const setProp = (prop) => (items) => {
        return setFilters((filters) => ({
            ...filters,
            [prop]: items,
        }));
    };

    const setQuestions = setProp("questions");
    const setThemes = setProp("themes");

    return (
        <>
            <Button
                variant="text"
                className="w-full justify-start p-0 mt-4"
                onClick={() => setFiltersOpen(!filtersOpen)}
            >
                {/* Gobby Assist ON */}
                {!filtersOpen ? (
                    // Closed: responses left, "Filter Options" and arrow right
                    <div className="flex w-full items-center justify-between">
                        <Typography variant="paragraph" className="text-black">
                            {responseNoDisplay} of{" "}
                            {customResponsesFilterPager?.total_entries || 0}{" "}
                            Responses
                        </Typography>
                        <span className="flex items-center gap-2">
                            <Typography
                                variant="paragraph"
                                className=" text-black"
                            >
                                Filter Options
                            </Typography>
                            <MaterialIcon
                                name="keyboard_arrow_down"
                                color="black"
                            />
                        </span>
                    </div>
                ) : (
                    // Open: only arrow right
                    <div className="flex w-full items-center justify-end">
                        <MaterialIcon name="keyboard_arrow_up" color="black" />
                    </div>
                )}
            </Button>
            {/* TODO: These dont currently have their options deleted when they are gone from filter */}
            <Collapse
                open={filtersOpen}
                className={`space-y-4 mt-4  ${
                    filtersOpen ? "overflow-visible" : ""
                }`}
            >
                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-4">
                    {/* Questions */}
                    {!!displayedQuestions && (
                        <div className="flex-1 min-w-48 relative">
                            <PopoverCheckboxes
                                isSelected={questionsIsSelected}
                                listItemContent={({ position, text, nanoid }) => (
                                    <div className="flex justify-between items-center w-full gap-1">
                                        <Typography>{position}. {text}</Typography>
                                        {(dropdownQuestionCounts[nanoid] || 0) > 0 && <Typography className="pr-2">({dropdownQuestionCounts[nanoid] || 0})</Typography>}
                                    </div>
                                )}
                                buttonContent="Choose Questions"
                                items={displayedQuestions}
                                onSubmit={setQuestions}
                                onUnthemedApply={() => {}}
                            />
                        </div>
                    )}

                    {/* Themes */}
                    <div className="flex-1 min-w-48 relative">
                        <PopoverCheckboxes
                            isSelected={themesIsSelected}
                            listItemContent={({ name, id }) => (
                                <div className="flex justify-between items-center w-full gap-1">
                                    <Typography>{name}</Typography>
                                    {(dropdownThemeCounts[id] || 0) > 0 && <Typography>({dropdownThemeCounts[id] || 0})</Typography>}
                                </div>
                            )}
                            buttonContent="Choose Themes"
                            items={themesData?.data ?? []}
                            onSubmit={setThemes}
                            showUnthemed={true}
                            unthemedChecked={filters?.unthemed || false}
                            onUnthemedApply={(checked) => setFilters((prev) => ({ ...prev, unthemed: checked }))}
                            counter={counter}
                            itemAction={(item) => (
                                <IconButton
                                    variant="text"
                                    className="p-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenOptions(item);
                                    }}
                                >
                                    <MaterialIcon
                                        name="more_vert"
                                        color="black"
                                    />
                                </IconButton>
                            )}
                        />
                    </div>

                    {/* Text Search */}
                    <div className="flex-1 min-w-64 relative">
                        <Input
                            variant="outlined"
                            placeholder="Search"
                            label="Search"
                            name="search"
                            value={textSearch}
                            onChange={(e) => setTextSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault(); // prevent native submit
                                    handleAddSearch(e, {
                                        search: textSearch,
                                    });
                                    setTextSearch(""); // optional: clear input after submit
                                }
                            }}
                            className="z-10 relative"
                        />
                    </div>
                </div>

                {/* Active Filters */}
                <FilterChips
                    responseNoDisplay={responseNoDisplay}
                    totalEntries={customResponsesFilterPager?.total_entries}
                    filters={filters}
                    onClear={emptyFilter}
                    onRemoveFilter={removeFilter}
                    onToggleUnthemed={(checked) =>
                        setFilters((prev) => ({ ...prev, unthemed: checked }))
                    }
                />

                {/* Edit/Delete pop up */}
                {selectedTheme && (
                    <Dialog
                        open={showThemeOptionsDialog}
                        handler={() => setShowThemeOptionsDialog(false)}
                        size="sm"
                        className="dialog"
                    >
                        <DialogHeader className="dialog__header text-black">
                            {selectedTheme?.name || "Theme"}
                        </DialogHeader>
                        <DialogBody className="dialog__body">
                            <div className="dialog__section flex flex-row">
                                <Button
                                    className="btn btn--primary sm:w-auto"
                                    onClick={handleOpenEdit}
                                >
                                    Edit
                                </Button>
                                <Button
                                    className="btn btn--danger sm:w-auto"
                                    onClick={handleOpenDelete}
                                >
                                    Remove
                                </Button>
                            </div>
                        </DialogBody>
                        <DialogFooter className="dialog__footer">
                            <Button
                                variant="text"
                                onClick={() => setShowThemeOptionsDialog(false)}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </Dialog>
                )}
                {/* Edit Theme Dialog */}
                {showEditThemeDialog && selectedTheme && (
                    <EditThemeDialog
                        open={showEditThemeDialog}
                        onClose={() => setShowEditThemeDialog(false)}
                        theme={selectedTheme}
                        onSaveThemeName={(id, newName) => {
                            handleEditThemeName(id, newName);
                            setShowEditThemeDialog(false);
                        }}
                    />
                )}
                {/* DELETE Theme Dialog */}
                {showDeleteDialog && selectedTheme && (
                    <Dialog
                        open={showDeleteDialog}
                        handler={() => setShowDeleteDialog(false)}
                        size="sm"
                        className="dialog"
                    >
                        <DialogHeader className="dialog__header">
                            <Typography variant="h5" color="red">
                                Confirm Removal
                            </Typography>
                        </DialogHeader>

                        <DialogBody className="dialog__body">
                            <Typography variant="paragraph" color="gray">
                                This will permenantely remove{" "}
                                {themeToDelete?.name || "this theme"}?
                            </Typography>
                        </DialogBody>

                        <DialogFooter className="dialog__footer">
                            <Button
                                className="btn btn--secondary"
                                onClick={() => setShowDeleteDialog(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="btn btn--danger"
                                onClick={() => {
                                    handleDeleteTheme(themeToDelete?.id);
                                    setShowDeleteDialog(false);
                                }}
                            >
                                Remove
                            </Button>
                        </DialogFooter>
                    </Dialog>
                )}
            </Collapse>
        </>
    );
}
